/**
 * Export utilities — builds CSV and TSV strings from grid rows.
 *
 * Two flattening strategies:
 *   flattenVisibleRows  — iterates the TanStack visible row model ("export what you see")
 *   flattenAllRows      — recursively walks the raw row tree ("export all data")
 *
 * Both produce positional arrays ([level, name, ...cellValues]) to avoid
 * key collisions when multiple columns share the same header text.
 */

const INDENT = '  '

/** Formats a raw cell value for export, delegating to the app formatter when available. */
function formatCell (value, format, formatter) {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (formatter) return formatter(value, format)
  return String(value)
}

/** Converts a single row into a positional array: [level, indented name, ...cell values]. */
function rowToArray (level, name, depth, columnDefs, rowData, formatter) {
  const cells = [level, `${INDENT.repeat(depth)}${name}`]
  for (const col of columnDefs) {
    const raw = rowData[col.accessorKey]
    cells.push(formatCell(raw, col.format, formatter))
  }
  return cells
}

/** Flattens the TanStack visible row model into export arrays (respects expand state). */
export function flattenVisibleRows (tanstackRows, columnDefs, formatter, totalRow) {
  const result = []

  for (const row of tanstackRows) {
    if (row.original?._placeholder) continue
    const name = row.original?.name ?? ''
    const level = row.original?.grouping ?? ''
    result.push(rowToArray(level, name, row.depth, columnDefs, row.original, formatter))
    // Benchmarks are not part of the TanStack subRow model; they render
    // immediately under any visible parent row regardless of expand state
    // (DDPTable sibling-row semantics). Mirror that here so visible-export
    // matches what the user sees.
    if (row.original?.benchmarks?.length) {
      for (const b of row.original.benchmarks) {
        result.push(rowToArray('Benchmark', b.name ?? '', row.depth + 1, columnDefs, b, formatter))
      }
    }
  }

  if (totalRow) {
    result.push(rowToArray('Household', totalRow.name || 'Total', 0, columnDefs, totalRow, formatter))
    if (totalRow.benchmarks?.length) {
      for (const b of totalRow.benchmarks) {
        result.push(rowToArray('Benchmark', b.name ?? '', 0, columnDefs, b, formatter))
      }
    }
  }

  return result
}

/** Recursively walks the raw row tree into export arrays (ignores expand state). */
export function flattenAllRows (rawRowTree, columnDefs, formatter, totalRow) {
  const result = []

  function walk (rows, depth) {
    if (!rows) return
    for (const row of rows) {
      if (row._placeholder) continue
      result.push(rowToArray(row.grouping ?? '', row.name ?? '', depth, columnDefs, row, formatter))
      if (row.benchmarks?.length) {
        for (const b of row.benchmarks) {
          result.push(rowToArray('Benchmark', b.name ?? '', depth + 1, columnDefs, b, formatter))
        }
      }
      if (row.data) walk(row.data, depth + 1)
    }
  }

  walk(rawRowTree, 0)

  if (totalRow) {
    result.push(rowToArray('Household', totalRow.name || 'Total', 0, columnDefs, totalRow, formatter))
    if (totalRow.benchmarks?.length) {
      for (const b of totalRow.benchmarks) {
        result.push(rowToArray('Benchmark', b.name ?? '', 0, columnDefs, b, formatter))
      }
    }
  }

  return result
}

/** Wraps value in double-quotes if it contains commas, quotes, or newlines (RFC 4180). */
function escapeCSV (value) {
  const str = String(value ?? '')
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** Builds the header row: [Level, Name, ...column headers]. */
export function buildHeaders (columnDefs) {
  return ['Level', 'Name', ...columnDefs.map(c => c.header)]
}

/** Assembles a complete CSV string from flat row arrays and a header row. */
export function buildCSV (flatRows, headers) {
  const lines = [headers.map(escapeCSV).join(',')]
  for (const row of flatRows) {
    lines.push(row.map(cell => escapeCSV(cell)).join(','))
  }
  return lines.join('\n')
}

/** Replaces tabs and newlines with spaces so TSV structure isn't broken on paste. */
function escapeTSV (value) {
  return String(value ?? '').replace(/[\t\r\n]/g, ' ')
}

/** Assembles a complete TSV string from flat row arrays and a header row. */
export function buildTSV (flatRows, headers) {
  const lines = [headers.map(escapeTSV).join('\t')]
  for (const row of flatRows) {
    lines.push(row.map(escapeTSV).join('\t'))
  }
  return lines.join('\n')
}
